import { useState } from 'react';
import Modal from './Modal';

const DURATA_STANDARD_ORE = 3;

// Inizio e fine della serata in ora di Roma, calcolati in un posto solo per
// Google e Outlook. Senza ora di fine: tre ore. Fine prima dell'inizio
// ("21:00 -> 01:00"): il giorno dopo. Senza orario: tutto il giorno.
function orari(event) {
  const ok = (t) => !!t && /^\d{1,2}:\d{2}/.test(t);
  const piuGiorni = (data, n) => {
    const d = new Date(`${data}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };
  if (!ok(event.time_start)) {
    return { tuttoIlGiorno: true, dataInizio: event.date, dataFine: piuGiorni(event.date, 1) };
  }
  const inizio = event.time_start.slice(0, 5).padStart(5, '0');
  let fine;
  let dataFine = event.date;
  if (ok(event.time_end)) {
    fine = event.time_end.slice(0, 5).padStart(5, '0');
    if (fine <= inizio) dataFine = piuGiorni(event.date, 1);
  } else {
    const [h, m] = inizio.split(':').map(Number);
    const tot = h * 60 + m + DURATA_STANDARD_ORE * 60;
    fine = `${String(Math.floor(tot / 60) % 24).padStart(2, '0')}:${String(tot % 60).padStart(2, '0')}`;
    if (tot >= 24 * 60) dataFine = piuGiorni(event.date, 1);
  }
  return { tuttoIlGiorno: false, dataInizio: event.date, inizio, dataFine, fine };
}

// Di quanto Roma e' avanti rispetto a UTC in quella data e ora ("+01:00" in
// inverno, "+02:00" con l'ora legale). Serve a Outlook, che senza fuso
// userebbe quello impostato nell'account.
function fusoRoma(data, ora) {
  const [y, mo, d] = data.split('-').map(Number);
  const [h, mi] = ora.split(':').map(Number);
  const ipotesi = Date.UTC(y, mo - 1, d, h, mi);
  const v = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Rome', hourCycle: 'h23', year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date(ipotesi)).map(x => [x.type, x.value]));
  const minuti = Math.round((Date.UTC(+v.year, +v.month - 1, +v.day, +v.hour, +v.minute) - ipotesi) / 60000);
  return `+${String(Math.floor(minuti / 60)).padStart(2, '0')}:${String(minuti % 60).padStart(2, '0')}`;
}

const luogoDi = (event, venue) => [venue?.name || event.location_name, venue?.address || event.location, event.city || venue?.city]
  .filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', ');
const dettagliDi = (event) => `Tutto sulla serata, brani e partecipanti:\nhttps://jammo.live/e/${event.id}`;

// Google Calendar con l'evento gia' compilato. Gli orari restano quelli di
// Roma (parametro ctz), senza conversioni.
function linkGoogle(event, venue) {
  const o = orari(event);
  const compatta = (data, ora) => data.replace(/-/g, '') + (ora ? `T${ora.replace(':', '')}00` : '');
  const date = o.tuttoIlGiorno
    ? `${compatta(o.dataInizio)}/${compatta(o.dataFine)}`
    : `${compatta(o.dataInizio, o.inizio)}/${compatta(o.dataFine, o.fine)}`;
  const p = new URLSearchParams({
    action: 'TEMPLATE', text: event.title || 'Serata Jammo', dates: date,
    ctz: 'Europe/Rome', details: dettagliDi(event),
  });
  const luogo = luogoDi(event, venue);
  if (luogo) p.set('location', luogo);
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

// Outlook di lavoro (Microsoft 365): apre il nuovo evento gia' compilato in
// Outlook online, che poi compare anche nell'app Outlook e sul computer.
function linkOutlook(event, venue) {
  const o = orari(event);
  const p = new URLSearchParams({
    path: '/calendar/action/compose', rru: 'addevent',
    subject: event.title || 'Serata Jammo', body: dettagliDi(event),
  });
  if (o.tuttoIlGiorno) {
    p.set('allday', 'true'); p.set('startdt', o.dataInizio); p.set('enddt', o.dataFine);
  } else {
    p.set('startdt', `${o.dataInizio}T${o.inizio}:00${fusoRoma(o.dataInizio, o.inizio)}`);
    p.set('enddt', `${o.dataFine}T${o.fine}:00${fusoRoma(o.dataFine, o.fine)}`);
  }
  const luogo = luogoDi(event, venue);
  if (luogo) p.set('location', luogo);
  return `https://outlook.office.com/calendar/0/deeplink/compose?${p.toString()}`;
}

const scelta = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%',
  minHeight: 52, padding: '12px 16px', borderRadius: 12, boxSizing: 'border-box',
  fontSize: 18, fontWeight: 800, textDecoration: 'none', fontFamily: 'inherit',
};

// Nella web app installata sulla Home (iPhone), un link verso jammo.live
// resta dentro la web app, che non sa aprire un .ics: non succede niente.
// Un indirizzo diverso invece l'iPhone lo apre in Safari, dove il
// Calendario parte. jamup.vercel.app e' lo stesso progetto Vercel, quindi la
// stessa funzione: cambia solo l'indirizzo.
function linkIcs(eventId) {
  const installata = typeof window !== 'undefined' && (
    window.navigator.standalone === true
    || window.matchMedia?.('(display-mode: standalone)').matches);
  return installata ? `https://jamup.vercel.app/cal/${eventId}` : `/cal/${eventId}`;
}

function SceltaCalendario({ event, venue, titolo, sottotitolo, onClose }) {
  // La finestra si chiude un attimo dopo il tocco: chiuderla subito toglie il
  // link dalla pagina mentre l'iPhone lo sta ancora aprendo.
  const chiudiDopo = () => setTimeout(onClose, 400);
  return (
    <Modal onClose={onClose} maxWidth={380} centered>
      <div className="modal-title">{titolo || 'Aggiungi al calendario'}</div>
      {sottotitolo && (
        <div style={{ fontSize: 17, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 16 }}>{sottotitolo}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Il .ics lo genera il server (api/calendario.js): l'iPhone apre
            la scheda "Aggiungi" del Calendario di sistema. Funziona anche con
            Outlook per Mac/Windows, ma sull'iPhone apre sempre il Calendario
            di Apple: per l'app Outlook c'e' il link sotto. */}
        <a href={linkIcs(event.id)} target="_blank" rel="noreferrer" onClick={chiudiDopo}
          style={{ ...scelta, background: '#ED4D1D', color: '#fff' }}>
          iPhone e Apple
        </a>
        <a href={linkGoogle(event, venue)} target="_blank" rel="noreferrer" onClick={chiudiDopo}
          style={{ ...scelta, background: 'rgba(237,77,29,0.08)', color: 'var(--brand)', border: '1px solid rgba(237,77,29,0.3)' }}>
          Google Calendar
        </a>
        <a href={linkOutlook(event, venue)} target="_blank" rel="noreferrer" onClick={chiudiDopo}
          style={{ ...scelta, background: 'rgba(237,77,29,0.08)', color: 'var(--brand)', border: '1px solid rgba(237,77,29,0.3)' }}>
          Outlook
        </a>
        <button onClick={onClose}
          style={{ ...scelta, background: 'none', border: 'none', color: 'var(--muted)', fontWeight: 600, cursor: 'pointer' }}>
          {sottotitolo ? 'No, grazie' : 'Annulla'}
        </button>
      </div>
      <div style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.5, marginTop: 12 }}>
        Con un promemoria il giorno prima. Se l'orario cambia, va aggiunta di nuovo.
      </div>
    </Modal>
  );
}

// Pillola arancione chiara: la stessa forma di "Mappa", affiancata a lei nel
// box della serata. Esportata perche' Mappa usi esattamente la stessa.
export const PILLOLA = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  minHeight: 44, padding: '0 18px', borderRadius: 999, cursor: 'pointer', boxSizing: 'border-box',
  fontSize: 16, fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap',
  background: 'rgba(237,77,29,0.08)', color: 'var(--brand)', border: '1px solid rgba(237,77,29,0.3)',
};

// Il pulsante. "variante" sceglie l'aspetto: un link discreto dentro una
// scheda, o un pulsante pieno.
function AddToCalendar({ event, venue, variante = 'link', style, etichetta = 'Aggiungi al calendario' }) {
  const [aperto, setAperto] = useState(false);
  if (!event?.date) return null;
  const aspetto = variante === 'pillola'
    ? PILLOLA
    : variante === 'pulsante'
    ? { width: '100%', minHeight: 48, padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
        fontSize: 18, fontWeight: 700, fontFamily: 'inherit',
        background: 'rgba(237,77,29,0.08)', color: 'var(--brand)', border: '1px solid rgba(237,77,29,0.3)' }
    : { minHeight: 44, padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 17, fontWeight: 700, color: 'var(--brand)', fontFamily: 'inherit' };
  return (
    <>
      <button onClick={() => setAperto(true)} style={{ ...aspetto, ...style }}>
        {etichetta}
      </button>
      {aperto && <SceltaCalendario event={event} venue={venue} onClose={() => setAperto(false)} />}
    </>
  );
}

export default AddToCalendar;
